import { Component, Input } from '@angular/core';

export type PdfAiParserFileRowState = 'idle' | 'uploading' | 'failed';

/**
 * One 48px hairline file row used by the AI order parser surfaces (single-parse
 * dropzone collapse, multi-file queue, inbox upload strip). Shows a type-mapped
 * monochrome file icon, the filename in mono, the size as a caption and the
 * per-file upload state. Actions (replace/remove) are content-projected via
 * `[file-row-actions]`.
 */
@Component({
  selector: 'lib-pdf-ai-parser-file-row',
  standalone: false,
  templateUrl: './pdf-ai-parser-file-row.component.html',
  styleUrl: './pdf-ai-parser-file-row.component.scss'
})
export class PdfAiParserFileRowComponent {
  @Input() fileName = '';
  /** File size in bytes; the size caption is hidden when not provided. */
  @Input() fileSize?: number;
  @Input() state: PdfAiParserFileRowState = 'idle';
  /** Shown as a danger caption when state is 'failed'. */
  @Input() errorMessage?: string;

  /** Monochrome FA icon mapped from the filename extension. */
  get iconClass(): string {
    const lower = (this.fileName || '').toLowerCase();
    if (lower.endsWith('.pdf')) {
      return 'fa-file-pdf';
    }
    if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
      return 'fa-file-excel';
    }
    if (lower.endsWith('.csv')) {
      return 'fa-file-csv';
    }
    if (['.png', '.jpg', '.jpeg', '.gif', '.webp'].some(ext => lower.endsWith(ext))) {
      return 'fa-file-image';
    }
    return 'fa-file';
  }

  /** Human-readable size, e.g. "123.4 KB". */
  get sizeLabel(): string {
    if (this.fileSize == null) {
      return '';
    }
    const kb = this.fileSize / 1024;
    return kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb.toFixed(1)} KB`;
  }
}
